import { c as _c } from "react/compiler-runtime";
import { BILY_EVENT, trackIdentifiedEvent, trackOrderCompleted } from '@/components/bily-tracking';
import { useSubscription } from '@/context/SubscriptionProvider';
import { useBilyAnalyticsMarks } from '@/hooks/useBilyAnalyticsMarks';
import { useBilyCustomer } from '@/hooks/useBilyIdentify';
import { ApiClient } from '@/services/api-client';
import { useAuth } from '@clerk/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * The Bily events that describe an account's life, reported once per org.
 *
 * They all live here rather than on the page where they happen, for two
 * reasons. The moment is not always on screen: a renewal is charged while
 * nobody is looking, and a channel can finish connecting in a popup. And
 * "once per org" needs one place that owns the marks, otherwise every page
 * grows its own half-correct guard, which is how the old localStorage
 * bookkeeping ended up counting the same payment once per device.
 *
 * Mount once, inside Clerk and the subscription provider.
 */

/** Query keys shared with useOnboardingTutorial, so React Query serves one request. */
const WHATSAPP_PROFILES_KEY = 'integration.getWhatsappProfiles';
const INSTAGRAM_ACCOUNTS_KEY = 'integration.getInstagramAccounts';
export const BILY_MARK = {
  activated: 'bily:activated',
  trialStarted: 'bily:trial_started',
  subscriptionStarted: 'bily:subscription_started',
  order: (invoiceId: string) => `bily:order:${invoiceId}`
} as const;
export function useBilyLifecycleReporter() {
  const $ = _c(30);
  const {
    orgId
  } = useAuth();
  const customer = useBilyCustomer();
  const {
    subscription
  } = useSubscription();
  const marks = useBilyAnalyticsMarks();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [WHATSAPP_PROFILES_KEY];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = !!orgId;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const whatsapp = useQuery(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [INSTAGRAM_ACCOUNTS_KEY];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!orgId;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: _temp2,
      enabled: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const instagram = useQuery(t5);
  let t6;
  if ($[6] !== whatsapp.data?.data) {
    t6 = whatsapp.data?.data ?? [];
    $[6] = whatsapp.data?.data;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = t6.filter(_temp3);
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const whatsappActive = t7.length;
  let t8;
  if ($[10] !== instagram.data?.data?.Accounts) {
    t8 = instagram.data?.data?.Accounts ?? [];
    $[10] = instagram.data?.data?.Accounts;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const instagramCount = t8.length;
  const channelsConnected = whatsappActive + instagramCount;
  let t10;
  let t9;
  if ($[12] !== channelsConnected || $[13] !== customer || $[14] !== marks || $[15] !== whatsappActive) {
    t9 = () => {
      if (!customer || !marks.lifecycleEnabled) {
        return;
      }
      if (channelsConnected === 0) {
        return;
      }
      marks.report(BILY_MARK.activated, () => {
        trackIdentifiedEvent(BILY_EVENT.activated, customer.user, customer.account, {
          bily_business_event: "Activated",
          bily_funnel_stage: "activation",
          bily_optimization_role: "primary",
          channel: whatsappActive > 0 ? "whatsapp" : "instagram",
          channels_connected: channelsConnected
        });
      });
    };
    t10 = [customer, marks, channelsConnected, whatsappActive];
    $[12] = channelsConnected;
    $[13] = customer;
    $[14] = marks;
    $[15] = whatsappActive;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  const payment = subscription?.LastPayment;
  let t11;
  if ($[18] !== customer || $[19] !== marks || $[20] !== payment || $[21] !== subscription?.ProductId || $[22] !== subscription?.Status) {
    t11 = () => {
      if (!customer || !payment?.InvoiceId) {
        return;
      }
      const total = payment.AmountInCents / 100;
      const currency = payment.Currency.toUpperCase();
      const account = {
        ...customer.account,
        plan: subscription?.ProductId,
        subscriptionStatus: subscription?.Status
      };
      marks.report(BILY_MARK.order(payment.InvoiceId), () => {
        trackOrderCompleted({
          id: payment.InvoiceId,
          currency,
          total,
          products: [{
            id: subscription?.ProductId ?? "orsay_subscription",
            name: payment.Description ?? "Orsay subscription",
            price: total,
            currency,
            quantity: 1
          }]
        }, customer.user, account, {
          bily_standard_event: "Purchase",
          bily_business_event: payment.IsFirstPayment ? "First Payment" : "Recurring Payment",
          bily_funnel_stage: "conversion",
          bily_optimization_role: "primary",
          is_first_payment: payment.IsFirstPayment,
          paid_at: payment.PaidAt
        });
      });
      if (!payment.IsFirstPayment) {
        return;
      }
      marks.report(BILY_MARK.subscriptionStarted, () => {
        trackIdentifiedEvent(BILY_EVENT.subscriptionStarted, customer.user, account, {
          bily_standard_event: "Subscribe",
          bily_business_event: "Subscription Started",
          bily_funnel_stage: "conversion",
          bily_optimization_role: "primary",
          order_id: payment.InvoiceId,
          value: total,
          currency
        });
      });
    };
    $[18] = customer;
    $[19] = marks;
    $[20] = payment;
    $[21] = subscription?.ProductId;
    $[22] = subscription?.Status;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = subscription?.ProductId;
  const t13 = subscription?.Status;
  let t14;
  if ($[24] !== customer || $[25] !== marks || $[26] !== payment || $[27] !== t12 || $[28] !== t13) {
    t14 = [customer, marks, payment, t12, t13];
    $[24] = customer;
    $[25] = marks;
    $[26] = payment;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  useEffect(t11, t14);
}

/** Renders nothing; exists so the hook can be mounted from the provider tree. */
function _temp3(profile) {
  return profile?.ProfileStatus === "ACTIVE";
}
function _temp2() {
  return ApiClient.integration.getInstagramAccounts();
}
function _temp() {
  return ApiClient.integration.getWhatsappProfiles();
}
export function BilyLifecycleReporter() {
  useBilyLifecycleReporter();
  return null;
}